import { motion } from "framer-motion";
import { Cpu, Keyboard, Camera, Globe, Lock } from "lucide-react";

const features = [
  {
    title: "AI-Powered Typing",
    icon: <Cpu className="w-12 h-12 text-white" />,
    image: "/gesture_ai.jpg",
    details:
      "Type accurately and seamlessly using AI-driven hand gesture recognition. Perfect for multitasking, eating, or hands-free typing.",
  },
  {
    title: "Virtual Keyboard",
    icon: <Keyboard className="w-12 h-12 text-white" />,
    image: "/virtual_keyboard.jpg",
    details:
      "Access a full virtual keyboard from any device. No need for physical hardware, making it versatile for home, office, or travel.",
  },
  {
    title: "Camera Based",
    icon: <Camera className="w-12 h-12 text-white" />,
    image: "/webcam.jpg",
    details:
      "Simply enable your webcam and start typing. Works on laptops, desktops, or even tablets without installing anything.",
  },
  {
    title: "Anywhere Typing",
    icon: <Globe className="w-12 h-12 text-white" />,
    image: "/hands_free.jpg",
    details:
      "Type while eating, cooking, or multitasking. Ideal for scenarios where touching a keyboard isn’t convenient.",
  },
  {
    title: "Privacy Focused",
    icon: <Lock className="w-12 h-12 text-white" />,
    image: "/privacy.jpg",
    details:
      "All hand gesture data is processed locally on your device. No sensitive data leaves your computer, ensuring full privacy.",
  },
];

export default function Features() {
  return (
    <section className="w-full py-24 bg-gray-900">
      <div className="max-w-7xl mx-auto px-6 text-center">
        <h2 className="text-4xl font-bold mb-16 text-white">
          Why Choose Our Virtual Keyboard?
        </h2>

        {/* First row: 3 cards */}
        <div className="flex justify-center gap-10 flex-wrap mb-10">
          {features.slice(0, 3).map((feature, idx) => (
            <motion.div
              key={idx}
              className="relative w-80 h-80 [perspective:1000px] cursor-pointer"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: idx * 0.2 }}
              viewport={{ once: true }}
            >
              <motion.div
                className="relative w-full h-full [transform-style:preserve-3d] transition-transform duration-700"
                whileHover={{ rotateY: 180 }}
              >
                {/* FRONT */}
                <div
                  className="absolute w-full h-full [backface-visibility:hidden] rounded-xl shadow-xl bg-cover bg-center flex flex-col items-center justify-center text-center"
                  style={{ backgroundImage: `url(${feature.image})` }}
                >
                  <div className="bg-black bg-opacity-60 p-6 rounded-xl flex flex-col items-center justify-center">
                    {feature.icon}
                    <h3 className="text-xl font-bold mt-4 text-white">
                      {feature.title}
                    </h3>
                  </div>
                </div>

                {/* BACK */}
                <div
                  className="absolute w-full h-full [backface-visibility:hidden] [transform:rotateY(180deg)] rounded-xl shadow-xl bg-cover bg-center flex items-center justify-center p-6"
                  style={{ backgroundImage: `url(${feature.image})` }}
                >
                  <div className="bg-black bg-opacity-60 p-4 rounded-lg text-center">
                    <h3 className="text-xl font-bold text-white mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-white text-base">{feature.details}</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* Second row: 2 cards, centered */}
        <div className="flex justify-center gap-10 flex-wrap">
          {features.slice(3).map((feature, idx) => (
            <motion.div
              key={`row2-${idx}`}
              className="relative w-80 h-80 [perspective:1000px] cursor-pointer"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: idx * 0.2 }}
              viewport={{ once: true }}
            >
              <motion.div
                className="relative w-full h-full [transform-style:preserve-3d] transition-transform duration-700"
                whileHover={{ rotateY: 180 }}
              >
                {/* FRONT */}
                <div
                  className="absolute w-full h-full [backface-visibility:hidden] rounded-xl shadow-xl bg-cover bg-center flex flex-col items-center justify-center text-center"
                  style={{ backgroundImage: `url(${feature.image})` }}
                >
                  <div className="bg-black bg-opacity-60 p-6 rounded-xl flex flex-col items-center justify-center">
                    {feature.icon}
                    <h3 className="text-xl font-bold mt-4 text-white">
                      {feature.title}
                    </h3>
                  </div>
                </div>

                {/* BACK */}
                <div
                  className="absolute w-full h-full [backface-visibility:hidden] [transform:rotateY(180deg)] rounded-xl shadow-xl bg-cover bg-center flex items-center justify-center p-6"
                  style={{ backgroundImage: `url(${feature.image})` }}
                >
                  <div className="bg-black bg-opacity-60 p-4 rounded-lg text-center">
                    <h3 className="text-xl font-bold text-white mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-white text-base">{feature.details}</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
